import React, { createContext, useContext, useState, useEffect } from "react";

const CartContext = createContext(null);
const CART_KEY = "mayachitro-cart-v1";

export function CartProvider({ children }) {
  const [cart, setCart] = useState([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CART_KEY);
      if (stored) {
        setCart(JSON.parse(stored));
      }
    } catch (e) {
      console.error("Failed to load cart from localStorage", e);
    }
  }, []);

  const saveCart = (nextCart) => {
    setCart(nextCart);
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(nextCart));
    } catch (e) {
      console.error("Failed to save cart to localStorage", e);
    }
    // Dispatch event for other parts of the app
    window.dispatchEvent(new Event("mayachitro-cart-change"));
  };

  const addToCart = (product, license) => {
    const item = {
      productId: product.productId,
      slug: product.slug,
      title: product.title,
      licenseLabel: license.label,
      price: Number(license.price || product.price || 0),
      quantity: 1, // backend strictly requires 1 for digital downloads
    };

    // Remove any existing entry with the same product + license to avoid duplicate
    const filtered = cart.filter(
      (entry) => !(entry.productId === item.productId && entry.licenseLabel === item.licenseLabel)
    );
    const nextCart = [...filtered, item];
    saveCart(nextCart);
  };

  const removeFromCart = (productId, licenseLabel) => {
    const nextCart = cart.filter(
      (entry) => !(entry.productId === productId && entry.licenseLabel === licenseLabel)
    );
    saveCart(nextCart);
  };

  const clearCart = () => {
    saveCart([]);
  };

  const cartCount = cart.length;

  const cartTotal = cart.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        clearCart,
        cartCount,
        cartTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
